import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import { ClosingTextInput as TimeReportClosingTextInput } from "../../time-reports/TimeReportClosingText.js";
import { OpeningTextInput as TimeReportOpeningTextInput } from "../../time-reports/TimeReportOpeningText.js";
import { ModifyOrganisationTextsModal } from "./ModifyOrganisationTextsModal.js";
import {
  useModifyOrganisationTextsModalQuery,
  useOrganisationTextsModal_ModifyOrganisationMutation,
} from "./ModifyOrganisationTextsModal.generated.js";

interface FormValues {
  opening?: string;
  closing?: string;
}

interface Props {
  organisationId: string;
  title: string;
  handleClose: () => void;
  handleComplete?: (organisationId: string) => Promise<void>;
}

export const ModifyOrganisationTimeReportsTextsModal = ({
  organisationId,
  title,
  handleClose,
  handleComplete,
}: React.PropsWithChildren<Props>) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const query = useModifyOrganisationTextsModalQuery({
    client,
  });
  const [modifyOrganisation] =
    useOrganisationTextsModal_ModifyOrganisationMutation({
      client,
    });

  const organisationDocumentSettings = getDataOrNull(
    query.data?.organisationDocumentSettings
  );

  const onSubmit = async (values: FormValues) => {
    try {
      await modifyOrganisation({
        variables: {
          input: {
            timeReportTexts: {
              opening: values.opening,
              closing: values.closing,
            },
          },
        },
      });
      enqueueSnackbar(
        t("Changes saved", {
          ns: "Global",
        })
      );
      handleComplete?.(organisationId);
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  if (viewer.organisation.id !== organisationId)
    return <div>Not own organisation!</div>;

  if (!organisationDocumentSettings) return null;

  return (
    <ModifyOrganisationTextsModal
      handleClose={handleClose}
      isLoading={query.loading}
      organisationId={organisationId}
      title={title}
    >
      <Stack spacing={3} direction="column">
        <Stack direction="column" spacing={1}>
          <Typography variant="h3">
            {t("Opening text", {
              ns: "OrganisationSettings",
            })}
          </Typography>
          <TimeReportOpeningTextInput
            content={organisationDocumentSettings.timeReportTexts.opening}
            onSave={content => onSubmit(content)}
            showInfo={true}
          />
        </Stack>
        <Stack direction="column" spacing={1}>
          <Typography variant="h3">
            {t("Closing text", {
              ns: "OrganisationSettings",
            })}
          </Typography>
          <TimeReportClosingTextInput
            content={organisationDocumentSettings.timeReportTexts.closing}
            onSave={content => onSubmit(content)}
            showInfo={true}
          />
        </Stack>
      </Stack>
    </ModifyOrganisationTextsModal>
  );
};
